<template> 
  <Modal :show="true" @close-modal="closeModal" class="team-modal" @click.stop>
    <div class="team-modal__content">
      <div class="team-modal__header">
        <div class="team-modal__info">
          <div class="team-modal__icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/>
            </svg>
          </div>
          <div>
            <h2 class="team-modal__title">{{ role.name }}</h2>
            <p class="team-modal__subtitle">
              <span class="team-modal__count">{{ role.teamMembers.length }}</span> members assigned to this role
            </p>
          </div>
        </div>
      </div>

      <div class="team-modal__table-wrapper">
        <table class="team-table">
          <thead class="team-table__header">
            <tr>
              <th class="team-table__th">Name</th>
              <th class="team-table__th">Date Added</th>
              <th class="team-table__th">Email Address</th>
              <th class="team-table__th team-table__th--actions">Actions</th>
            </tr>
          </thead>
          <tbody class="team-table__body">
            <tr
              v-for="(user, index) in role.teamMembers"
              :key="index"
              class="team-table__row"
            >
              <td class="team-table__td">
                <div class="team-table__name">
                  <div class="team-table__avatar">
                    {{ getInitials(user.fname, user.lname) }}
                  </div>
                  <span>{{ user.fname }} {{ user.lname }}</span>
                </div>
              </td>
              <td class="team-table__td">
                <span class="team-table__date">
                  {{ formatISODate(user.createdAt) }}
                </span>
              </td>
              <td class="team-table__td">
                <span class="team-table__email">{{ user.email }}</span>
              </td>
              <td class="team-table__td team-table__td--actions">
                <Popper offsetDistance="5" placement="bottom-end">
                  <button class="team-table__actions-btn">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" fill="currentColor"/>
                    </svg>
                  </button>
                  <template #content>
                    <div class="team-table__dropdown">
                      <button
                        @click.stop="removeUser(user.id, role.id)"
                        class="team-table__dropdown-item team-table__dropdown-item--danger"
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8 1.33333C4.32 1.33333 1.33333 4.32 1.33333 8C1.33333 11.68 4.32 14.6667 8 14.6667C11.68 14.6667 14.6667 11.68 14.6667 8C14.6667 4.32 11.68 1.33333 8 1.33333ZM11.3333 10.3933L10.3933 11.3333L8 8.94L5.60667 11.3333L4.66667 10.3933L7.06 8L4.66667 5.60667L5.60667 4.66667L8 7.06L10.3933 4.66667L11.3333 5.60667L8.94 8L11.3333 10.3933Z" fill="currentColor"/>
                        </svg>
                        Remove Member
                      </button>
                    </div>
                  </template>
                </Popper>
              </td>
            </tr>
            <tr v-if="loading">
              <td colspan="4" class="team-table__loading">
                <div class="team-table__spinner"></div>
              </td>
            </tr>
            <tr v-if="!loading && role.teamMembers.length === 0">
              <td colspan="4" class="team-table__empty">
                <div class="team-table__empty-content">
                  <div class="team-table__empty-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                  <p>No members have been assigned to this role yet</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="team-modal__footer">
        <AddTeamMemberButton title="Add Member" :addteam="true" />
      </div>
    </div>
  </Modal>
</template>
  
<script setup>
import { ref } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import Modal from "@/components/general/Modal";
import AddTeamMemberButton from "@/components/teams/AddTeamMemberButton.vue";
import Popper from "vue3-popper";
import { formatISODate, formatISOTime } from "@/utils/helpers.js";

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  role: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["close-modal", "success"]);

const router = useRouter();
const store = useStore();
const loading = ref(false);
const activeUserMenu = ref(null);

const getInitials = (firstName, lastName) => {
  const first = firstName ? firstName.charAt(0).toUpperCase() : "";
  const last = lastName ? lastName.charAt(0).toUpperCase() : "";
  return `${first}${last}`;
};

const closeModal = () => {
  emit("close-modal");
  activeUserMenu.value = null;
};

const toggleUserActions = (index) => {
  if (activeUserMenu.value === index) {
    activeUserMenu.value = null;
  } else {
    activeUserMenu.value = index;
  }
};

const removeUser = async (memberId, roleId) => {
  loading.value = true;
  try {
    await store.dispatch("organisationModule/deleteTeamMember", {
      memberId,
      roleId,
    });
    emit("success");
  } catch (error) {
    console.error("Failed to remove team member:", error);
  } finally {
    loading.value = false;
  }
};

const navigateToCreateRole = () => {
  router.push("/dashboard/teams/create-role");
};

const formatDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return d.toLocaleDateString();
};
</script>

<style lang="scss" scoped>
.team-modal {
  &__content {
    width: 100%;
    max-width: 720px;
    padding: 24px;
  }

  &__header {
    margin-bottom: 24px;
    padding-bottom: 20px;
    border-bottom: 1px solid #E2E8F0;
  }

  &__info {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, #4D84FF 0%, #6B9AFF 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    flex-shrink: 0;
  }

  &__title {
    font-family: 'DM Sans', sans-serif;
    font-size: 20px;
    font-weight: 500;
    color: #0F172A;
    margin: 0 0 4px 0;
  }

  &__subtitle {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
    margin: 0;
  }

  &__count {
    font-weight: 500;
    color: #4D84FF;
  }

  &__table-wrapper {
    overflow-x: auto;
    max-height: 320px;
    overflow-y: auto;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    margin-bottom: 20px;
  }

  &__footer {
    padding-top: 16px;
    display: flex;
    justify-content: flex-end;
  }
}

.team-table {
  width: 100%;
  border-collapse: collapse;

  &__header {
    background: #F8FAFC;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  &__th {
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: #64748B;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    text-align: left;
    padding: 12px 16px;
    white-space: nowrap;
    border-bottom: 1px solid #E2E8F0;

    &--actions {
      text-align: right;
      width: 80px;
    }
  }

  &__body {
    background: white;
  }

  &__row {
    transition: background 0.15s ease;

    &:hover {
      background: #F8FAFC;
    }

    &:not(:last-child) {
      border-bottom: 1px solid #E2E8F0;
    }
  }

  &__td {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #0F172A;
    padding: 14px 16px;
    white-space: nowrap;

    &--actions {
      text-align: right;
    }
  }

  &__name {
    display: flex;
    align-items: center;
    gap: 12px;
    font-weight: 500;
  }

  &__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, #4D84FF 0%, #6B9AFF 100%);
    color: white;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__date {
    color: #64748B;
    font-size: 13px;
  }

  &__email {
    color: #64748B;
    font-size: 13px;
  }

  &__actions-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    background: transparent;
    color: #64748B;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease;

    &:hover {
      background: #F1F5F9;
      color: #0F172A;
    }
  }

  &__dropdown {
    background: white;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    padding: 6px;
    min-width: 180px;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
  }

  &__dropdown-item {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border: none;
    background: transparent;
    border-radius: 6px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #0F172A;
    cursor: pointer;
    transition: all 0.15s ease;
    text-align: left;

    &:hover {
      background: #F8FAFC;
    }

    &--danger {
      color: #EF4444;

      &:hover {
        background: #FEF2F2;
      }
    }
  }

  &__loading {
    padding: 32px 16px;
    text-align: center;
  }

  &__spinner {
    width: 28px;
    height: 28px;
    border: 3px solid #E2E8F0;
    border-top-color: #4D84FF;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin: 0 auto;
  }

  &__empty {
    padding: 48px 20px;
  }

  &__empty-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    
    p {
      font-family: 'DM Sans', sans-serif;
      font-size: 14px;
      font-weight: 400;
      color: #64748B;
      margin: 0;
    }
  }

  &__empty-icon {
    width: 48px;
    height: 48px;
    background: #F1F5F9;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #64748B;
    margin-bottom: 12px;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// Modal backdrop override
:deep(.modal-content) {
  border-radius: 12px;
}
</style>